import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Redirect } from 'react-router-dom';
import { useCookies } from "react-cookie";

import { getPathByRoute } from 'App.js';
import routeKeys from 'CustomerRouteKeys.js';
import Toast, { TYPE_SUCCESS } from "components/Toast.js";
import CmsContentList from 'components/data/CmsContentList.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import CmsContentRendered from 'components/data/CmsContentRendered.js';
import { useTransitAccount } from 'components/data/transit-account/TransitAccount.js';
import { isFreeTravelTA } from "utils/transit-account-utils/StatusUtils.js";

import Container from 'components/Container.js';
import Button, { buttonTypeStylePlain, Secondary } from 'components/Button.js';
import { useGlobalToastsContext } from "context/ToastProvider.js";
import TripHistoryTable from 'components/account/data-tables/TripHistoryTable.js';
import PurchaseHistoryTable from 'components/account/data-tables/PurchaseHistoryTable.js';
import Tabs, { tabsPrimary } from 'components/Tabs.js';
import RegisterForAccount from 'components/banners/RegisterForAccount.js';
import BenefitsOfCharlieAccount from 'components/banners/BenefitsOfCharlieAccount.js';
import CardSummary from 'components/account/CardSummary.js';
import TokenNameAndPan from 'components/account/card/TokenNameAndPan.js';
import ContactCustomerService from 'components/banners/ContactCustomerService.js';
import { findPrimaryToken } from 'components/manage-cards/TokenHelpers.js';
import MissedTap from 'components/banners/MissedTap.js';
import SuspendedForNegativeBalance from 'components/banners/SuspendedForNegativeBalance.js';
import {
	useTransitAccountIdContext,
} from "context/TransitAccountIdContext.js";
import TapsAlertsProvider, {
	TapsAlertsContext,
} from "context/TapsAlertsContext.js";
import PurseBalanceProvider, { usePurseBalanceContext } from 'context/PurseBalanceContext.js';
import PublicAppVars from 'utils/PublicAppVars.js';
import { getViewTransactionHistoryBreadcrumbs } from './ViewTransactionHistory.js';
import { useBreadcrumbCallback } from 'context/BreadcrumbProvider.js';
import { SelectedCardName } from 'layouts/CardLayout.js';
import {
	POST_SIGN_IN_UPGRADE_CARD_TRANSIT_ACOUNT_ID,
	cookieOptions,
} from "components/data/session-user/PostSigninGoToPath.js";
import { useModalContext } from 'context/ModalProvider.js';
import ConfirmReplaceCard from 'components/modals/ConfirmReplace.js';
import { useIsEmvCard } from 'components/data/transit-account/EMV.helpers.js';
import { ComponentLoading } from 'components/icons/LoadingIcon.js';

import * as admin from 'layouts/Admin.module.css';
import * as tabStyle from 'components/Tabs.module.css';
import { OPEN_TRANSIT_RESTRICTED_CARD, OPEN_TRANSIT_TEMPORARY_CARD } from 'server/api-types/WSSubsystemAccountToken.js';


const cms = {
	recentRides: 'miscText.guest-recent-trips',
	purchaseHistory: 'miscText.guest-recent-purchases',

	checkAnother: 'miscText.guest-sidebar-check-another',
	loadPassesOrBalance: 'miscText.cardmenu-purchase',

	replace: 'miscText.guest-sidebar-replace-cta',
	upgrade: 'miscText.guest-sidebar-upgrade-cta',

	upgradeCardToast: 'miscText.guest-forcereg-upgrade',
	replaceCardToast: 'miscText.guest-forcereg-replace',
};

export const getCardOverviewWithDataBreadcrumbs = () => [
	...getViewTransactionHistoryBreadcrumbs(),
	<SelectedCardName
		key="selectedCardNameOverview"
		routeKey={routeKeys.GuestCardOverview}
	/>,
];

const CardOverview = ({ subsystemAccountReference, transitAccountQ }) => {
	const { setToast, removeToast } = useGlobalToastsContext();
	const { hasNegativeBalance, loading: purseContextLoading } = usePurseBalanceContext();
	const [ redirect, setRedirect ] = useState(false);

	const isEmvCard = useIsEmvCard({ subsystemAccountReference });

	const { setModal } = useModalContext();
	const [ _, setCookie ] = useCookies();

	const registerRoute = <Redirect push to={{ pathname: getPathByRoute(routeKeys.Register) }} />;

	const tabs = [
		{
			key: 'trip-history',
			label: <CmsContentRenderer.Span contentKey={cms.recentRides} fallbackValue="Recent trips" />,
			content: <TripHistoryTable showFilters={false} isUnregistered limit={PublicAppVars.CARD_OVERVIEW_RECENT_TRIPS_LIMIT} />,
		},
		{
			key: 'purchase-history',
			label: <CmsContentRenderer.Span contentKey={cms.purchaseHistory} fallbackValue="Recent purchases" />,
			content: <PurchaseHistoryTable showFilters={false} isUnregistered />,
		},
	];

	useEffect(() => {
		if (!subsystemAccountReference) {
			setRedirect(
				<Redirect push to={{ pathname: getPathByRoute(routeKeys.ViewTransactionHistory) }}/>
			);
		}
	}, [ subsystemAccountReference ]);

	if (redirect) {
		return redirect;
	}

	// Upgrade card flow
	const handleUpgradeCookie = () => {
		if (PublicAppVars.ENABLE_UPGRADE_CARD) {
			setCookie(POST_SIGN_IN_UPGRADE_CARD_TRANSIT_ACOUNT_ID, subsystemAccountReference, cookieOptions);

			setToast(<Toast
				type={TYPE_SUCCESS}
				title={<CmsContentRendered
					contentKey={cms.upgradeCardToast}
					fallbackValue="After registering your Charlie account, you will be prompted to order your upgraded Charlie Card."
				/>}
				onClosed={removeToast}
			/>);
		}
		setRedirect(registerRoute);
	};

	const freeTravelTA = isFreeTravelTA(transitAccountQ);
	const primaryToken = findPrimaryToken(transitAccountQ.tokens);
	const primaryTokenInfo = primaryToken.tokenInfo;

	const isTempCard = primaryToken.mediaType === OPEN_TRANSIT_TEMPORARY_CARD;
	const canReplace = !isTempCard && primaryToken.mediaType !== OPEN_TRANSIT_RESTRICTED_CARD;

	return (
		<CmsContentList list={Object.values(cms)}>{() => (
			<>
				{purseContextLoading
					? null
					: hasNegativeBalance
						? <SuspendedForNegativeBalance subsystemAccountReference={subsystemAccountReference} />
						: <TapsAlertsProvider>
							<TapsAlertsContext.Consumer>{({ tapsAlerts }) => (
								tapsAlerts?.length
									? <MissedTap />
									: <RegisterForAccount />
							)}</TapsAlertsContext.Consumer>
						</TapsAlertsProvider>
				}
				<Container overrideClass={admin.container}>
					<section className={admin.main}>
						<div className={admin.header}>
							<h1 className={admin.pageTitle} data-qa="AdminPageTitle">
								{primaryTokenInfo
									? <TokenNameAndPan mediaType={primaryToken.mediaType} tokenInfo={primaryTokenInfo} />
									: null}
							</h1>
							<CardSummary />
							<Tabs {...{ tabs }}
								tabStyle={tabsPrimary}
								additionalClasses={tabStyle.guest}
							/>
						</div>
					</section>

					<div className={admin.rightNav}>
						<Button
							type={Secondary}
							additionalClassNames={admin.sidebarItem}
							to={getPathByRoute(routeKeys.ViewTransactionHistory)}
						>
							<CmsContentRenderer.Span
								contentKey={cms.checkAnother}
								fallbackValue="Check Another Card"
							/>
						</Button>
						{!isEmvCard && <>
							{freeTravelTA
								? null
								: <Button
									isPrimary
									to={{
										pathname: getPathByRoute(routeKeys.AccountPurchaseProduct, { transit_account_id: subsystemAccountReference }),
										state: { purchase: true },
									}}
									additionalClassNames={admin.sidebarItem}
								>
									<CmsContentRenderer.Span
										contentKey={cms.loadPassesOrBalance}
										fallbackValue="Load Passes or Balance"
									/>
								</Button>}
							{canReplace &&
								<Button
									typeStyle={buttonTypeStylePlain}
									onClick={() =>
										setModal(<ConfirmReplaceCard subsystemAccountReference={subsystemAccountReference} onConfirm={() => setRedirect(registerRoute)} />)
									}
									additionalClassNames={cx(admin.sidebarLink, admin.sidebarItem)}
								>
									<CmsContentRenderer.Span
										contentKey={cms.replace}
										fallbackValue="Replace your Charlie Card"
									/>
								</Button>
							}
						</>}
						{PublicAppVars.ENABLE_UPGRADE_CARD && isTempCard &&
							<Button
								typeStyle={buttonTypeStylePlain}
								onClick={handleUpgradeCookie}
								additionalClassNames={cx(admin.sidebarLink, admin.sidebarItem)}
							>
								<CmsContentRenderer.Span
									contentKey={cms.upgrade}
									fallbackValue="Upgrade to Standard Fare Card" />
							</Button>
						}
					</div>
				</Container>

				<BenefitsOfCharlieAccount />
				<ContactCustomerService {...{ subsystemAccountReference, isUnregistered: true }} />
			</>
		)}</CmsContentList>
	);
};

const CardOverviewWithData = () => {
	const subsystemAccountReference = useTransitAccountIdContext();

	useBreadcrumbCallback(getCardOverviewWithDataBreadcrumbs);

	const {
		data,
		loading,
	} = useTransitAccount({ subsystemAccountReference });

	if (loading) {
		return <ComponentLoading />;
	}

	return (
		<PurseBalanceProvider subsystemAccountReference={subsystemAccountReference}>
			<CardOverview
				subsystemAccountReference={subsystemAccountReference}
				transitAccountQ={data.transitAccountQ}
			/>
		</PurseBalanceProvider>
	);
};
export default CardOverviewWithData;
